/**
 * Logic Apps Expression Functions
 *
 * Complete list of workflow expression functions with descriptions,
 * signatures, and examples for autocomplete and documentation.
 */

export interface ExpressionFunctionInfo {
  /** Function name */
  name: string;
  /** Short description */
  description: string;
  /** Function signature */
  signature: string;
  /** Category for grouping */
  category: ExpressionFunctionCategory;
  /** Example usage */
  example: string;
  /** Expected return type */
  returnType: string;
  /** Parameter descriptions */
  parameters?: ExpressionParameter[];
}

export interface ExpressionParameter {
  name: string;
  type: string;
  description: string;
  required?: boolean;
}

export type ExpressionFunctionCategory =
  | 'string'
  | 'collection'
  | 'logical'
  | 'conversion'
  | 'math'
  | 'datetime'
  | 'reference'
  | 'workflow'
  | 'uriParsing'
  | 'manipulation';

export const EXPRESSION_FUNCTIONS: ExpressionFunctionInfo[] = [
  // =============================================================================
  // STRING FUNCTIONS
  // =============================================================================
  {
    name: 'concat',
    description: 'Combines two or more strings into one string',
    signature: "concat(string1, string2, ...)",
    category: 'string',
    example: "concat('Hello', ' ', 'World')",
    returnType: 'string',
    parameters: [
      { name: 'string1', type: 'string', description: 'First string to concatenate', required: true },
      { name: 'stringN', type: 'string', description: 'Additional strings to concatenate' },
    ],
  },
  {
    name: 'substring',
    description: 'Returns characters from a string starting at a specified position',
    signature: 'substring(text, startIndex, length?)',
    category: 'string',
    example: "substring('hello world', 0, 5)",
    returnType: 'string',
    parameters: [
      { name: 'text', type: 'string', description: 'The source string', required: true },
      { name: 'startIndex', type: 'integer', description: 'Zero-based start position', required: true },
      { name: 'length', type: 'integer', description: 'Number of characters to return' },
    ],
  },
  {
    name: 'replace',
    description: 'Replace a substring with another string',
    signature: 'replace(text, oldText, newText)',
    category: 'string',
    example: "replace('Hello World', 'World', 'Universe')",
    returnType: 'string',
    parameters: [
      { name: 'text', type: 'string', description: 'The source string', required: true },
      { name: 'oldText', type: 'string', description: 'The text to find', required: true },
      { name: 'newText', type: 'string', description: 'The replacement text', required: true },
    ],
  },
  {
    name: 'split',
    description: 'Split a string into an array of substrings',
    signature: 'split(text, delimiter)',
    category: 'string',
    example: "split('a,b,c', ',')",
    returnType: 'array',
    parameters: [
      { name: 'text', type: 'string', description: 'The string to split', required: true },
      { name: 'delimiter', type: 'string', description: 'The delimiter to split on', required: true },
    ],
  },
  {
    name: 'trim',
    description: 'Remove leading and trailing whitespace',
    signature: 'trim(text)',
    category: 'string',
    example: "trim('  hello  ')",
    returnType: 'string',
    parameters: [
      { name: 'text', type: 'string', description: 'The string to trim', required: true },
    ],
  },
  {
    name: 'toLower',
    description: 'Convert a string to lowercase',
    signature: 'toLower(text)',
    category: 'string',
    example: "toLower('HELLO')",
    returnType: 'string',
    parameters: [
      { name: 'text', type: 'string', description: 'The string to convert', required: true },
    ],
  },
  {
    name: 'toUpper',
    description: 'Convert a string to uppercase',
    signature: 'toUpper(text)',
    category: 'string',
    example: "toUpper('hello')",
    returnType: 'string',
    parameters: [
      { name: 'text', type: 'string', description: 'The string to convert', required: true },
    ],
  },
  {
    name: 'indexOf',
    description: 'Return the index of a substring',
    signature: 'indexOf(text, searchText)',
    category: 'string',
    example: "indexOf('hello world', 'world')",
    returnType: 'integer',
    parameters: [
      { name: 'text', type: 'string', description: 'The string to search', required: true },
      { name: 'searchText', type: 'string', description: 'The text to find', required: true },
    ],
  },
  {
    name: 'lastIndexOf',
    description: 'Return the last index of a substring',
    signature: 'lastIndexOf(text, searchText)',
    category: 'string',
    example: "lastIndexOf('hello world world', 'world')",
    returnType: 'integer',
    parameters: [
      { name: 'text', type: 'string', description: 'The string to search', required: true },
      { name: 'searchText', type: 'string', description: 'The text to find', required: true },
    ],
  },
  {
    name: 'startsWith',
    description: 'Check if a string starts with a specified substring',
    signature: 'startsWith(text, searchText)',
    category: 'string',
    example: "startsWith('hello world', 'hello')",
    returnType: 'boolean',
    parameters: [
      { name: 'text', type: 'string', description: 'The string to check', required: true },
      { name: 'searchText', type: 'string', description: 'The prefix to look for', required: true },
    ],
  },
  {
    name: 'endsWith',
    description: 'Check if a string ends with a specified substring',
    signature: 'endsWith(text, searchText)',
    category: 'string',
    example: "endsWith('hello world', 'world')",
    returnType: 'boolean',
    parameters: [
      { name: 'text', type: 'string', description: 'The string to check', required: true },
      { name: 'searchText', type: 'string', description: 'The suffix to look for', required: true },
    ],
  },
  {
    name: 'contains',
    description: 'Check if a string contains a substring',
    signature: 'contains(text, searchText)',
    category: 'string',
    example: "contains('hello world', 'world')",
    returnType: 'boolean',
    parameters: [
      { name: 'text', type: 'string', description: 'The string to search', required: true },
      { name: 'searchText', type: 'string', description: 'The text to find', required: true },
    ],
  },
  {
    name: 'length',
    description: 'Return the length of a string or array',
    signature: 'length(value)',
    category: 'string',
    example: "length('hello')",
    returnType: 'integer',
    parameters: [
      { name: 'value', type: 'string|array', description: 'The string or array to measure', required: true },
    ],
  },
  {
    name: 'guid',
    description: 'Generate a globally unique identifier (GUID)',
    signature: 'guid(format?)',
    category: 'string',
    example: "guid('D')",
    returnType: 'string',
    parameters: [
      { name: 'format', type: 'string', description: 'Format specifier: N, D, B, P, or X' },
    ],
  },
  {
    name: 'formatNumber',
    description: 'Format a number as a string with specific format',
    signature: 'formatNumber(number, format, locale?)',
    category: 'string',
    example: "formatNumber(1234567.89, 'N2', 'en-US')",
    returnType: 'string',
    parameters: [
      { name: 'number', type: 'number', description: 'The number to format', required: true },
      { name: 'format', type: 'string', description: 'Format string (N, C, P, etc.)', required: true },
      { name: 'locale', type: 'string', description: 'Locale for formatting' },
    ],
  },
  {
    name: 'nthIndexOf',
    description: 'Returns the index of the nth occurrence of a substring',
    signature: 'nthIndexOf(text, searchText, occurrence)',
    category: 'string',
    example: "nthIndexOf('hello world hello', 'hello', 2) // 12",
    returnType: 'number',
    parameters: [
      { name: 'text', type: 'string', description: 'The source string to search', required: true },
      { name: 'searchText', type: 'string', description: 'The substring to find', required: true },
      { name: 'occurrence', type: 'integer', description: 'Which occurrence to find (1-based)', required: true },
    ],
  },
  {
    name: 'slice',
    description: 'Returns a section of a string defined by start and end index',
    signature: 'slice(text, startIndex, endIndex?)',
    category: 'string',
    example: '"Hello World".slice(0, 5) // "Hello"',
    returnType: 'string',
    parameters: [
      { name: 'text', type: 'string', description: 'The source string', required: true },
      { name: 'startIndex', type: 'integer', description: 'Zero-based start position', required: true },
      { name: 'endIndex', type: 'integer', description: 'Zero-based end position (exclusive)' },
    ],
  },
  {
    name: 'trimByteOrderMark',
    description: 'Removes the byte order mark (BOM) from a string',
    signature: 'trimByteOrderMark(text)',
    category: 'string',
    example: "text.replace(/^\\uFEFF/, '')",
    returnType: 'string',
    parameters: [
      { name: 'text', type: 'string', description: 'The string to trim BOM from', required: true },
    ],
  },
  {
    name: 'encodeXmlValue',
    description: 'Encodes special XML characters in a string',
    signature: 'encodeXmlValue(text)',
    category: 'string',
    example: "text.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;')",
    returnType: 'string',
    parameters: [
      { name: 'text', type: 'string', description: 'The string to XML-encode', required: true },
    ],
  },
  {
    name: 'decodeXmlValue',
    description: 'Decodes XML-encoded characters in a string',
    signature: 'decodeXmlValue(text)',
    category: 'string',
    example: "text.replace(/&lt;/g,'<').replace(/&gt;/g,'>').replace(/&amp;/g,'&')",
    returnType: 'string',
    parameters: [
      { name: 'text', type: 'string', description: 'The XML-encoded string to decode', required: true },
    ],
  },

  // =============================================================================
  // COLLECTION FUNCTIONS
  // =============================================================================
  {
    name: 'first',
    description: 'Return the first item from a collection',
    signature: 'first(collection)',
    category: 'collection',
    example: "first(createArray(1, 2, 3))",
    returnType: 'any',
    parameters: [
      { name: 'collection', type: 'array|string', description: 'The collection or string', required: true },
    ],
  },
  {
    name: 'last',
    description: 'Return the last item from a collection',
    signature: 'last(collection)',
    category: 'collection',
    example: "last(createArray(1, 2, 3))",
    returnType: 'any',
    parameters: [
      { name: 'collection', type: 'array|string', description: 'The collection or string', required: true },
    ],
  },
  {
    name: 'take',
    description: 'Return items from the front of a collection',
    signature: 'take(collection, count)',
    category: 'collection',
    example: "take(createArray(1, 2, 3, 4), 2)",
    returnType: 'array',
    parameters: [
      { name: 'collection', type: 'array|string', description: 'The collection', required: true },
      { name: 'count', type: 'integer', description: 'Number of items to take', required: true },
    ],
  },
  {
    name: 'skip',
    description: 'Remove items from the front of a collection',
    signature: 'skip(collection, count)',
    category: 'collection',
    example: "skip(createArray(1, 2, 3, 4), 2)",
    returnType: 'array',
    parameters: [
      { name: 'collection', type: 'array|string', description: 'The collection', required: true },
      { name: 'count', type: 'integer', description: 'Number of items to skip', required: true },
    ],
  },
  {
    name: 'join',
    description: 'Return a string with all items from an array joined by a delimiter',
    signature: 'join(collection, delimiter)',
    category: 'collection',
    example: "join(createArray('a', 'b', 'c'), ', ')",
    returnType: 'string',
    parameters: [
      { name: 'collection', type: 'array', description: 'The array to join', required: true },
      { name: 'delimiter', type: 'string', description: 'The delimiter string', required: true },
    ],
  },
  {
    name: 'union',
    description: 'Return a collection with all items from specified collections',
    signature: 'union(collection1, collection2, ...)',
    category: 'collection',
    example: "union(createArray(1, 2), createArray(2, 3))",
    returnType: 'array',
    parameters: [
      { name: 'collection1', type: 'array', description: 'First collection', required: true },
      { name: 'collection2', type: 'array', description: 'Second collection', required: true },
    ],
  },
  {
    name: 'intersection',
    description: 'Return a collection with only the common items',
    signature: 'intersection(collection1, collection2, ...)',
    category: 'collection',
    example: "intersection(createArray(1, 2, 3), createArray(2, 3, 4))",
    returnType: 'array',
    parameters: [
      { name: 'collection1', type: 'array', description: 'First collection', required: true },
      { name: 'collection2', type: 'array', description: 'Second collection', required: true },
    ],
  },
  {
    name: 'createArray',
    description: 'Create an array from the inputs',
    signature: 'createArray(items...)',
    category: 'collection',
    example: "createArray(1, 2, 3)",
    returnType: 'array',
    parameters: [
      { name: 'items', type: 'any', description: 'Items to add to the array', required: true },
    ],
  },
  {
    name: 'empty',
    description: 'Check whether a collection is empty',
    signature: 'empty(collection)',
    category: 'collection',
    example: "empty(createArray())",
    returnType: 'boolean',
    parameters: [
      { name: 'collection', type: 'array|string|object', description: 'The collection to check', required: true },
    ],
  },
  {
    name: 'sort',
    description: 'Sort items in an array',
    signature: 'sort(collection, sortBy?)',
    category: 'collection',
    example: "sort(createArray(3, 1, 2))",
    returnType: 'array',
    parameters: [
      { name: 'collection', type: 'array', description: 'The array to sort', required: true },
      { name: 'sortBy', type: 'string', description: 'Property to sort by (for objects)' },
    ],
  },
  {
    name: 'reverse',
    description: 'Reverse the order of items in an array',
    signature: 'reverse(collection)',
    category: 'collection',
    example: "reverse(createArray(1, 2, 3))",
    returnType: 'array',
    parameters: [
      { name: 'collection', type: 'array', description: 'The array to reverse', required: true },
    ],
  },
  {
    name: 'sortBy',
    description: 'Sorts an array of objects by a specified property',
    signature: 'sortBy(collection, key)',
    category: 'collection',
    example: "[...arr].sort((a, b) => a.name > b.name ? 1 : -1)",
    returnType: 'array',
    parameters: [
      { name: 'collection', type: 'array', description: 'The array of objects to sort', required: true },
      { name: 'key', type: 'string', description: 'The property name to sort by', required: true },
    ],
  },
  {
    name: 'chunk',
    description: 'Splits an array into groups of a specified size',
    signature: 'chunk(collection, size)',
    category: 'collection',
    example: "Array.from({length: Math.ceil(arr.length / 2)}, (_, i) => arr.slice(i * 2, (i + 1) * 2))",
    returnType: 'array',
    parameters: [
      { name: 'collection', type: 'array', description: 'The array to split into chunks', required: true },
      { name: 'size', type: 'integer', description: 'The size of each chunk', required: true },
    ],
  },

  // =============================================================================
  // LOGICAL FUNCTIONS
  // =============================================================================
  {
    name: 'if',
    description: 'Return one of two values based on a condition',
    signature: 'if(condition, trueValue, falseValue)',
    category: 'logical',
    example: "if(equals(1, 1), 'yes', 'no')",
    returnType: 'any',
    parameters: [
      { name: 'condition', type: 'boolean', description: 'The condition to evaluate', required: true },
      { name: 'trueValue', type: 'any', description: 'Value if condition is true', required: true },
      { name: 'falseValue', type: 'any', description: 'Value if condition is false', required: true },
    ],
  },
  {
    name: 'equals',
    description: 'Check whether two values are equal',
    signature: 'equals(value1, value2)',
    category: 'logical',
    example: 'equals(1, 1)',
    returnType: 'boolean',
    parameters: [
      { name: 'value1', type: 'any', description: 'First value', required: true },
      { name: 'value2', type: 'any', description: 'Second value', required: true },
    ],
  },
  {
    name: 'not',
    description: 'Return the opposite boolean value',
    signature: 'not(expression)',
    category: 'logical',
    example: 'not(equals(1, 2))',
    returnType: 'boolean',
    parameters: [
      { name: 'expression', type: 'boolean', description: 'The expression to negate', required: true },
    ],
  },
  {
    name: 'and',
    description: 'Check whether all expressions are true',
    signature: 'and(expression1, expression2, ...)',
    category: 'logical',
    example: 'and(equals(1, 1), equals(2, 2))',
    returnType: 'boolean',
    parameters: [
      { name: 'expression1', type: 'boolean', description: 'First expression', required: true },
      { name: 'expression2', type: 'boolean', description: 'Second expression', required: true },
    ],
  },
  {
    name: 'or',
    description: 'Check whether at least one expression is true',
    signature: 'or(expression1, expression2, ...)',
    category: 'logical',
    example: 'or(equals(1, 1), equals(1, 2))',
    returnType: 'boolean',
    parameters: [
      { name: 'expression1', type: 'boolean', description: 'First expression', required: true },
      { name: 'expression2', type: 'boolean', description: 'Second expression', required: true },
    ],
  },
  {
    name: 'greater',
    description: 'Check whether the first value is greater than the second',
    signature: 'greater(value1, value2)',
    category: 'logical',
    example: 'greater(5, 3)',
    returnType: 'boolean',
    parameters: [
      { name: 'value1', type: 'number|string', description: 'First value', required: true },
      { name: 'value2', type: 'number|string', description: 'Second value', required: true },
    ],
  },
  {
    name: 'greaterOrEquals',
    description: 'Check whether the first value is greater than or equal to the second',
    signature: 'greaterOrEquals(value1, value2)',
    category: 'logical',
    example: 'greaterOrEquals(5, 5)',
    returnType: 'boolean',
    parameters: [
      { name: 'value1', type: 'number|string', description: 'First value', required: true },
      { name: 'value2', type: 'number|string', description: 'Second value', required: true },
    ],
  },
  {
    name: 'less',
    description: 'Check whether the first value is less than the second',
    signature: 'less(value1, value2)',
    category: 'logical',
    example: 'less(3, 5)',
    returnType: 'boolean',
    parameters: [
      { name: 'value1', type: 'number|string', description: 'First value', required: true },
      { name: 'value2', type: 'number|string', description: 'Second value', required: true },
    ],
  },
  {
    name: 'lessOrEquals',
    description: 'Check whether the first value is less than or equal to the second',
    signature: 'lessOrEquals(value1, value2)',
    category: 'logical',
    example: 'lessOrEquals(5, 5)',
    returnType: 'boolean',
    parameters: [
      { name: 'value1', type: 'number|string', description: 'First value', required: true },
      { name: 'value2', type: 'number|string', description: 'Second value', required: true },
    ],
  },
  {
    name: 'coalesce',
    description: 'Return the first non-null value',
    signature: 'coalesce(value1, value2, ...)',
    category: 'logical',
    example: "coalesce(null, 'default')",
    returnType: 'any',
    parameters: [
      { name: 'value1', type: 'any', description: 'First value to check', required: true },
      { name: 'value2', type: 'any', description: 'Fallback value', required: true },
    ],
  },
  {
    name: 'isInt',
    description: 'Returns true if the value is an integer',
    signature: 'isInt(value)',
    category: 'logical',
    example: "Number.isInteger(42) // true",
    returnType: 'boolean',
    parameters: [
      { name: 'value', type: 'any', description: 'The value to check', required: true },
    ],
  },
  {
    name: 'isFloat',
    description: 'Returns true if the value is a floating-point number',
    signature: 'isFloat(value)',
    category: 'logical',
    example: "typeof value === 'number' && !Number.isInteger(value) // true for 3.14",
    returnType: 'boolean',
    parameters: [
      { name: 'value', type: 'any', description: 'The value to check', required: true },
    ],
  },

  // =============================================================================
  // CONVERSION FUNCTIONS
  // =============================================================================
  {
    name: 'json',
    description: 'Return the JSON type value or object from a string',
    signature: 'json(value)',
    category: 'conversion',
    example: 'json(\'{"name": "John"}\')',
    returnType: 'object',
    parameters: [
      { name: 'value', type: 'string', description: 'The JSON string to parse', required: true },
    ],
  },
  {
    name: 'string',
    description: 'Return the string version of a value',
    signature: 'string(value)',
    category: 'conversion',
    example: 'string(123)',
    returnType: 'string',
    parameters: [
      { name: 'value', type: 'any', description: 'The value to convert', required: true },
    ],
  },
  {
    name: 'int',
    description: 'Return the integer version of a value',
    signature: 'int(value)',
    category: 'conversion',
    example: "int('123')",
    returnType: 'integer',
    parameters: [
      { name: 'value', type: 'string|number', description: 'The value to convert', required: true },
    ],
  },
  {
    name: 'float',
    description: 'Return the floating-point version of a value',
    signature: 'float(value)',
    category: 'conversion',
    example: "float('123.45')",
    returnType: 'number',
    parameters: [
      { name: 'value', type: 'string|number', description: 'The value to convert', required: true },
    ],
  },
  {
    name: 'bool',
    description: 'Return the boolean version of a value',
    signature: 'bool(value)',
    category: 'conversion',
    example: "bool('true')",
    returnType: 'boolean',
    parameters: [
      { name: 'value', type: 'any', description: 'The value to convert', required: true },
    ],
  },
  {
    name: 'base64',
    description: 'Return the base64-encoded version of a string',
    signature: 'base64(value)',
    category: 'conversion',
    example: "base64('hello')",
    returnType: 'string',
    parameters: [
      { name: 'value', type: 'string', description: 'The string to encode', required: true },
    ],
  },
  {
    name: 'base64ToString',
    description: 'Return the string version of a base64-encoded string',
    signature: 'base64ToString(value)',
    category: 'conversion',
    example: "base64ToString('aGVsbG8=')",
    returnType: 'string',
    parameters: [
      { name: 'value', type: 'string', description: 'The base64 string to decode', required: true },
    ],
  },
  {
    name: 'dataUri',
    description: 'Return a data URI for a string',
    signature: 'dataUri(value)',
    category: 'conversion',
    example: "dataUri('hello')",
    returnType: 'string',
    parameters: [
      { name: 'value', type: 'string', description: 'The string to convert', required: true },
    ],
  },
  {
    name: 'dataUriToBinary',
    description: 'Return the binary version of a data URI',
    signature: 'dataUriToBinary(value)',
    category: 'conversion',
    example: "dataUriToBinary(dataUri('hello'))",
    returnType: 'binary',
    parameters: [
      { name: 'value', type: 'string', description: 'The data URI to convert', required: true },
    ],
  },
  {
    name: 'xml',
    description: 'Return the XML version of a JSON object',
    signature: 'xml(value)',
    category: 'conversion',
    example: 'xml(json(\'{"person": {"name": "John"}}\'))',
    returnType: 'xml',
    parameters: [
      { name: 'value', type: 'object', description: 'The object to convert', required: true },
    ],
  },
  {
    name: 'array',
    description: 'Converts the input to an array',
    signature: 'array(value)',
    category: 'conversion',
    example: "Array.from(value)",
    returnType: 'array',
    parameters: [
      { name: 'value', type: 'any', description: 'The value to convert to an array', required: true },
    ],
  },
  {
    name: 'binary',
    description: 'Converts a string to a binary representation',
    signature: 'binary(value)',
    category: 'conversion',
    example: "new TextEncoder().encode(value)",
    returnType: 'object',
    parameters: [
      { name: 'value', type: 'string', description: 'The string to convert to binary', required: true },
    ],
  },
  {
    name: 'base64ToBinary',
    description: 'Converts a base64 string to binary data',
    signature: 'base64ToBinary(value)',
    category: 'conversion',
    example: "Uint8Array.from(atob(value), c => c.charCodeAt(0))",
    returnType: 'object',
    parameters: [
      { name: 'value', type: 'string', description: 'The base64 string to convert', required: true },
    ],
  },
  {
    name: 'dataUriToString',
    description: 'Converts a data URI to a string',
    signature: 'dataUriToString(value)',
    category: 'conversion',
    example: "decodeURIComponent(value.split(',')[1])",
    returnType: 'string',
    parameters: [
      { name: 'value', type: 'string', description: 'The data URI to convert', required: true },
    ],
  },
  {
    name: 'decodeBase64',
    description: 'Decodes a base64-encoded string (alias for base64ToString)',
    signature: 'decodeBase64(value)',
    category: 'conversion',
    example: "atob(value)",
    returnType: 'string',
    parameters: [
      { name: 'value', type: 'string', description: 'The base64 string to decode', required: true },
    ],
  },
  {
    name: 'utf8Length',
    description: 'Returns the UTF-8 byte length of a string',
    signature: 'utf8Length(text)',
    category: 'conversion',
    example: "new TextEncoder().encode(text).length",
    returnType: 'number',
    parameters: [
      { name: 'text', type: 'string', description: 'The string to measure', required: true },
    ],
  },
  {
    name: 'utf16Length',
    description: 'Returns the UTF-16 code unit length of a string',
    signature: 'utf16Length(text)',
    category: 'conversion',
    example: "text.length",
    returnType: 'number',
    parameters: [
      { name: 'text', type: 'string', description: 'The string to measure', required: true },
    ],
  },
  {
    name: 'decimal',
    description: 'Converts a value to a decimal number with high precision',
    signature: 'decimal(value)',
    category: 'conversion',
    example: "parseFloat(value)",
    returnType: 'number',
    parameters: [
      { name: 'value', type: 'string|number', description: 'The value to convert to decimal', required: true },
    ],
  },
  {
    name: 'decodeDataUri',
    description: 'Decodes a data URI and returns its content',
    signature: 'decodeDataUri(value)',
    category: 'conversion',
    example: "atob(value.split(',')[1])",
    returnType: 'string',
    parameters: [
      { name: 'value', type: 'string', description: 'The data URI to decode', required: true },
    ],
  },
  {
    name: 'uriComponent',
    description: 'Encodes a string as a URI component',
    signature: 'uriComponent(value)',
    category: 'conversion',
    example: "encodeURIComponent(value)",
    returnType: 'string',
    parameters: [
      { name: 'value', type: 'string', description: 'The string to encode', required: true },
    ],
  },
  {
    name: 'uriComponentToBinary',
    description: 'Converts a URI component to binary',
    signature: 'uriComponentToBinary(value)',
    category: 'conversion',
    example: "new TextEncoder().encode(decodeURIComponent(value))",
    returnType: 'object',
    parameters: [
      { name: 'value', type: 'string', description: 'The URI-encoded string to convert', required: true },
    ],
  },
  {
    name: 'uriComponentToString',
    description: 'Decodes a URI component to string',
    signature: 'uriComponentToString(value)',
    category: 'conversion',
    example: "decodeURIComponent(value)",
    returnType: 'string',
    parameters: [
      { name: 'value', type: 'string', description: 'The URI-encoded string to decode', required: true },
    ],
  },

  // =============================================================================
  // MATH FUNCTIONS
  // =============================================================================
  {
    name: 'add',
    description: 'Return the sum of two numbers',
    signature: 'add(number1, number2)',
    category: 'math',
    example: 'add(1, 2)',
    returnType: 'number',
    parameters: [
      { name: 'number1', type: 'number', description: 'First number', required: true },
      { name: 'number2', type: 'number', description: 'Second number', required: true },
    ],
  },
  {
    name: 'sub',
    description: 'Return the difference of two numbers',
    signature: 'sub(number1, number2)',
    category: 'math',
    example: 'sub(5, 3)',
    returnType: 'number',
    parameters: [
      { name: 'number1', type: 'number', description: 'Number to subtract from', required: true },
      { name: 'number2', type: 'number', description: 'Number to subtract', required: true },
    ],
  },
  {
    name: 'mul',
    description: 'Return the product of two numbers',
    signature: 'mul(number1, number2)',
    category: 'math',
    example: 'mul(3, 4)',
    returnType: 'number',
    parameters: [
      { name: 'number1', type: 'number', description: 'First number', required: true },
      { name: 'number2', type: 'number', description: 'Second number', required: true },
    ],
  },
  {
    name: 'div',
    description: 'Return the quotient of two numbers',
    signature: 'div(number1, number2)',
    category: 'math',
    example: 'div(10, 2)',
    returnType: 'number',
    parameters: [
      { name: 'number1', type: 'number', description: 'Dividend', required: true },
      { name: 'number2', type: 'number', description: 'Divisor', required: true },
    ],
  },
  {
    name: 'mod',
    description: 'Return the remainder from dividing two numbers',
    signature: 'mod(number1, number2)',
    category: 'math',
    example: 'mod(10, 3)',
    returnType: 'number',
    parameters: [
      { name: 'number1', type: 'number', description: 'Dividend', required: true },
      { name: 'number2', type: 'number', description: 'Divisor', required: true },
    ],
  },
  {
    name: 'min',
    description: 'Return the minimum value from a set of numbers or array',
    signature: 'min(number1, number2, ...) or min(array)',
    category: 'math',
    example: 'min(1, 5, 3)',
    returnType: 'number',
    parameters: [
      { name: 'values', type: 'number[]|array', description: 'Numbers to compare', required: true },
    ],
  },
  {
    name: 'max',
    description: 'Return the maximum value from a set of numbers or array',
    signature: 'max(number1, number2, ...) or max(array)',
    category: 'math',
    example: 'max(1, 5, 3)',
    returnType: 'number',
    parameters: [
      { name: 'values', type: 'number[]|array', description: 'Numbers to compare', required: true },
    ],
  },
  {
    name: 'rand',
    description: 'Return a random integer from a specified range',
    signature: 'rand(minValue, maxValue)',
    category: 'math',
    example: 'rand(1, 100)',
    returnType: 'integer',
    parameters: [
      { name: 'minValue', type: 'integer', description: 'Minimum value (inclusive)', required: true },
      { name: 'maxValue', type: 'integer', description: 'Maximum value (exclusive)', required: true },
    ],
  },
  {
    name: 'range',
    description: 'Return an integer array starting from a specified number',
    signature: 'range(startIndex, count)',
    category: 'math',
    example: 'range(1, 5)',
    returnType: 'array',
    parameters: [
      { name: 'startIndex', type: 'integer', description: 'Starting number', required: true },
      { name: 'count', type: 'integer', description: 'Number of integers', required: true },
    ],
  },

  // =============================================================================
  // DATE AND TIME FUNCTIONS
  // =============================================================================
  {
    name: 'utcNow',
    description: 'Return the current UTC timestamp',
    signature: 'utcNow(format?)',
    category: 'datetime',
    example: 'utcNow()',
    returnType: 'string',
    parameters: [
      { name: 'format', type: 'string', description: 'Optional format string' },
    ],
  },
  {
    name: 'addDays',
    description: 'Add days to a timestamp',
    signature: 'addDays(timestamp, days, format?)',
    category: 'datetime',
    example: "addDays(utcNow(), 7)",
    returnType: 'string',
    parameters: [
      { name: 'timestamp', type: 'string', description: 'The source timestamp', required: true },
      { name: 'days', type: 'integer', description: 'Days to add', required: true },
      { name: 'format', type: 'string', description: 'Output format' },
    ],
  },
  {
    name: 'addHours',
    description: 'Add hours to a timestamp',
    signature: 'addHours(timestamp, hours, format?)',
    category: 'datetime',
    example: "addHours(utcNow(), 2)",
    returnType: 'string',
    parameters: [
      { name: 'timestamp', type: 'string', description: 'The source timestamp', required: true },
      { name: 'hours', type: 'integer', description: 'Hours to add', required: true },
      { name: 'format', type: 'string', description: 'Output format' },
    ],
  },
  {
    name: 'addMinutes',
    description: 'Add minutes to a timestamp',
    signature: 'addMinutes(timestamp, minutes, format?)',
    category: 'datetime',
    example: "addMinutes(utcNow(), 30)",
    returnType: 'string',
    parameters: [
      { name: 'timestamp', type: 'string', description: 'The source timestamp', required: true },
      { name: 'minutes', type: 'integer', description: 'Minutes to add', required: true },
      { name: 'format', type: 'string', description: 'Output format' },
    ],
  },
  {
    name: 'addSeconds',
    description: 'Add seconds to a timestamp',
    signature: 'addSeconds(timestamp, seconds, format?)',
    category: 'datetime',
    example: "addSeconds(utcNow(), 60)",
    returnType: 'string',
    parameters: [
      { name: 'timestamp', type: 'string', description: 'The source timestamp', required: true },
      { name: 'seconds', type: 'integer', description: 'Seconds to add', required: true },
      { name: 'format', type: 'string', description: 'Output format' },
    ],
  },
  {
    name: 'addToTime',
    description: 'Add a specified time unit to a timestamp',
    signature: "addToTime(timestamp, interval, timeUnit, format?)",
    category: 'datetime',
    example: "addToTime(utcNow(), 1, 'Day')",
    returnType: 'string',
    parameters: [
      { name: 'timestamp', type: 'string', description: 'The source timestamp', required: true },
      { name: 'interval', type: 'integer', description: 'Amount to add', required: true },
      { name: 'timeUnit', type: 'string', description: 'Unit: Second, Minute, Hour, Day, Week, Month, Year', required: true },
      { name: 'format', type: 'string', description: 'Output format' },
    ],
  },
  {
    name: 'subtractFromTime',
    description: 'Subtract a specified time unit from a timestamp',
    signature: "subtractFromTime(timestamp, interval, timeUnit, format?)",
    category: 'datetime',
    example: "subtractFromTime(utcNow(), 1, 'Day')",
    returnType: 'string',
    parameters: [
      { name: 'timestamp', type: 'string', description: 'The source timestamp', required: true },
      { name: 'interval', type: 'integer', description: 'Amount to subtract', required: true },
      { name: 'timeUnit', type: 'string', description: 'Unit: Second, Minute, Hour, Day, Week, Month, Year', required: true },
      { name: 'format', type: 'string', description: 'Output format' },
    ],
  },
  {
    name: 'formatDateTime',
    description: 'Return a timestamp in a specified format',
    signature: 'formatDateTime(timestamp, format?, locale?)',
    category: 'datetime',
    example: "formatDateTime(utcNow(), 'yyyy-MM-dd')",
    returnType: 'string',
    parameters: [
      { name: 'timestamp', type: 'string', description: 'The timestamp to format', required: true },
      { name: 'format', type: 'string', description: 'Format string' },
      { name: 'locale', type: 'string', description: 'Locale for formatting' },
    ],
  },
  {
    name: 'parseDateTime',
    description: 'Convert a string to a timestamp',
    signature: 'parseDateTime(value, locale?, format?)',
    category: 'datetime',
    example: "parseDateTime('2024-01-15')",
    returnType: 'string',
    parameters: [
      { name: 'value', type: 'string', description: 'The string to parse', required: true },
      { name: 'locale', type: 'string', description: 'Locale for parsing' },
      { name: 'format', type: 'string', description: 'Expected format' },
    ],
  },
  {
    name: 'startOfDay',
    description: 'Return the start of the day for a timestamp',
    signature: 'startOfDay(timestamp, format?)',
    category: 'datetime',
    example: "startOfDay(utcNow())",
    returnType: 'string',
    parameters: [
      { name: 'timestamp', type: 'string', description: 'The timestamp', required: true },
      { name: 'format', type: 'string', description: 'Output format' },
    ],
  },
  {
    name: 'startOfMonth',
    description: 'Return the start of the month for a timestamp',
    signature: 'startOfMonth(timestamp, format?)',
    category: 'datetime',
    example: "startOfMonth(utcNow())",
    returnType: 'string',
    parameters: [
      { name: 'timestamp', type: 'string', description: 'The timestamp', required: true },
      { name: 'format', type: 'string', description: 'Output format' },
    ],
  },
  {
    name: 'dayOfWeek',
    description: 'Return the day of week (0=Sunday)',
    signature: 'dayOfWeek(timestamp)',
    category: 'datetime',
    example: "dayOfWeek(utcNow())",
    returnType: 'integer',
    parameters: [
      { name: 'timestamp', type: 'string', description: 'The timestamp', required: true },
    ],
  },
  {
    name: 'dayOfMonth',
    description: 'Return the day of month',
    signature: 'dayOfMonth(timestamp)',
    category: 'datetime',
    example: "dayOfMonth(utcNow())",
    returnType: 'integer',
    parameters: [
      { name: 'timestamp', type: 'string', description: 'The timestamp', required: true },
    ],
  },
  {
    name: 'dayOfYear',
    description: 'Return the day of year',
    signature: 'dayOfYear(timestamp)',
    category: 'datetime',
    example: "dayOfYear(utcNow())",
    returnType: 'integer',
    parameters: [
      { name: 'timestamp', type: 'string', description: 'The timestamp', required: true },
    ],
  },
  {
    name: 'ticks',
    description: 'Return the ticks value for a timestamp',
    signature: 'ticks(timestamp)',
    category: 'datetime',
    example: "ticks(utcNow())",
    returnType: 'integer',
    parameters: [
      { name: 'timestamp', type: 'string', description: 'The timestamp', required: true },
    ],
  },
  {
    name: 'convertFromUtc',
    description: 'Convert a timestamp from UTC to a target time zone',
    signature: "convertFromUtc(timestamp, timezone, format?)",
    category: 'datetime',
    example: "convertFromUtc(utcNow(), 'Pacific Standard Time')",
    returnType: 'string',
    parameters: [
      { name: 'timestamp', type: 'string', description: 'The UTC timestamp', required: true },
      { name: 'timezone', type: 'string', description: 'Target time zone', required: true },
      { name: 'format', type: 'string', description: 'Output format' },
    ],
  },
  {
    name: 'convertToUtc',
    description: 'Convert a timestamp to UTC from a source time zone',
    signature: "convertToUtc(timestamp, timezone, format?)",
    category: 'datetime',
    example: "convertToUtc('2024-01-15T10:00:00', 'Pacific Standard Time')",
    returnType: 'string',
    parameters: [
      { name: 'timestamp', type: 'string', description: 'The source timestamp', required: true },
      { name: 'timezone', type: 'string', description: 'Source time zone', required: true },
      { name: 'format', type: 'string', description: 'Output format' },
    ],
  },
  {
    name: 'getFutureTime',
    description: 'Returns a timestamp for a specified time in the future',
    signature: 'getFutureTime(interval, timeUnit, format?)',
    category: 'datetime',
    example: "DateTime.now().plus({ hours: 2 }).toISO()",
    returnType: 'string',
    parameters: [
      { name: 'interval', type: 'integer', description: 'Number of time units to add', required: true },
      { name: 'timeUnit', type: 'string', description: 'Unit of time: Second, Minute, Hour, Day, Week, Month, Year', required: true },
      { name: 'format', type: 'string', description: 'Output format string' },
    ],
  },
  {
    name: 'getPastTime',
    description: 'Returns a timestamp for a specified time in the past',
    signature: 'getPastTime(interval, timeUnit, format?)',
    category: 'datetime',
    example: "DateTime.now().minus({ days: 3 }).toISO()",
    returnType: 'string',
    parameters: [
      { name: 'interval', type: 'integer', description: 'Number of time units to subtract', required: true },
      { name: 'timeUnit', type: 'string', description: 'Unit of time: Second, Minute, Hour, Day, Week, Month, Year', required: true },
      { name: 'format', type: 'string', description: 'Output format string' },
    ],
  },
  {
    name: 'dateDifference',
    description: 'Returns the difference between two timestamps',
    signature: 'dateDifference(startTimestamp, endTimestamp)',
    category: 'datetime',
    example: "DateTime.fromISO(end).diff(DateTime.fromISO(start), 'days').days",
    returnType: 'number',
    parameters: [
      { name: 'startTimestamp', type: 'string', description: 'The start timestamp', required: true },
      { name: 'endTimestamp', type: 'string', description: 'The end timestamp', required: true },
    ],
  },
  {
    name: 'convertTimeZone',
    description: 'Converts a timestamp from one timezone to another',
    signature: 'convertTimeZone(timestamp, sourceTimeZone, destinationTimeZone, format?)',
    category: 'datetime',
    example: "DateTime.fromISO(ts, {zone: src}).setZone(dst).toISO()",
    returnType: 'string',
    parameters: [
      { name: 'timestamp', type: 'string', description: 'The timestamp to convert', required: true },
      { name: 'sourceTimeZone', type: 'string', description: 'The source time zone', required: true },
      { name: 'destinationTimeZone', type: 'string', description: 'The destination time zone', required: true },
      { name: 'format', type: 'string', description: 'Output format string' },
    ],
  },
  {
    name: 'startOfHour',
    description: 'Returns the start of the hour for a timestamp',
    signature: 'startOfHour(timestamp, format?)',
    category: 'datetime',
    example: "DateTime.fromISO(ts).startOf('hour').toISO()",
    returnType: 'string',
    parameters: [
      { name: 'timestamp', type: 'string', description: 'The source timestamp', required: true },
      { name: 'format', type: 'string', description: 'Output format string' },
    ],
  },
  {
    name: 'formatTimeSpan',
    description: 'Formats a time span / duration string',
    signature: 'formatTimeSpan(timeSpan, format?)',
    category: 'datetime',
    example: 'Duration.fromISO(span).toFormat("hh:mm:ss")',
    returnType: 'string',
    parameters: [
      { name: 'timeSpan', type: 'string', description: 'The time span or duration string', required: true },
      { name: 'format', type: 'string', description: 'Output format string' },
    ],
  },

  // =============================================================================
  // REFERENCE FUNCTIONS
  // =============================================================================
  {
    name: 'parameters',
    description: 'Return the value of a workflow parameter',
    signature: "parameters('parameterName')",
    category: 'reference',
    example: "parameters('myParameter')",
    returnType: 'any',
    parameters: [
      { name: 'parameterName', type: 'string', description: 'Name of the parameter', required: true },
    ],
  },
  {
    name: 'variables',
    description: 'Return the value of a workflow variable',
    signature: "variables('variableName')",
    category: 'reference',
    example: "variables('myVariable')",
    returnType: 'any',
    parameters: [
      { name: 'variableName', type: 'string', description: 'Name of the variable', required: true },
    ],
  },
  {
    name: 'outputs',
    description: 'Return the outputs of an action',
    signature: "outputs('actionName')",
    category: 'reference',
    example: "outputs('Get_user')",
    returnType: 'object',
    parameters: [
      { name: 'actionName', type: 'string', description: 'Name of the action', required: true },
    ],
  },
  {
    name: 'body',
    description: 'Return the body output of an action',
    signature: "body('actionName')",
    category: 'reference',
    example: "body('Get_user')",
    returnType: 'any',
    parameters: [
      { name: 'actionName', type: 'string', description: 'Name of the action', required: true },
    ],
  },
  {
    name: 'triggerBody',
    description: 'Return the body of the trigger',
    signature: 'triggerBody()',
    category: 'reference',
    example: 'triggerBody()',
    returnType: 'any',
    parameters: [],
  },
  {
    name: 'triggerOutputs',
    description: 'Return the outputs of the trigger',
    signature: 'triggerOutputs()',
    category: 'reference',
    example: 'triggerOutputs()',
    returnType: 'object',
    parameters: [],
  },
  {
    name: 'item',
    description: "Return the current item in a For Each loop",
    signature: 'item()',
    category: 'reference',
    example: "item()['name']",
    returnType: 'any',
    parameters: [],
  },
  {
    name: 'items',
    description: 'Return the current item from a specific loop',
    signature: "items('loopName')",
    category: 'reference',
    example: "items('For_each_user')",
    returnType: 'any',
    parameters: [
      { name: 'loopName', type: 'string', description: 'Name of the loop', required: true },
    ],
  },
  {
    name: 'iterationIndexes',
    description: 'Return the current iteration index',
    signature: "iterationIndexes('loopName')",
    category: 'reference',
    example: "iterationIndexes('For_each_user')",
    returnType: 'integer',
    parameters: [
      { name: 'loopName', type: 'string', description: 'Name of the loop', required: true },
    ],
  },
  {
    name: 'result',
    description: 'Return the result of a scoped action',
    signature: "result('scopeName')",
    category: 'reference',
    example: "result('Scope_1')",
    returnType: 'array',
    parameters: [
      { name: 'scopeName', type: 'string', description: 'Name of the scope', required: true },
    ],
  },
  {
    name: 'actionOutputs',
    description: 'Return the outputs of an action at runtime',
    signature: "actionOutputs('actionName')",
    category: 'reference',
    example: "actionOutputs('Get_user')",
    returnType: 'object',
    parameters: [
      { name: 'actionName', type: 'string', description: 'Name of the action', required: true },
    ],
  },
  {
    name: 'actionBody',
    description: 'Return the body output of an action at runtime',
    signature: "actionBody('actionName')",
    category: 'reference',
    example: "actionBody('Get_user')",
    returnType: 'any',
    parameters: [
      { name: 'actionName', type: 'string', description: 'Name of the action', required: true },
    ],
  },

  // =============================================================================
  // WORKFLOW FUNCTIONS
  // =============================================================================
  {
    name: 'workflow',
    description: 'Return workflow run details',
    signature: 'workflow()',
    category: 'workflow',
    example: "workflow()['run']['name']",
    returnType: 'object',
    parameters: [],
  },
  {
    name: 'actions',
    description: 'Return the status of a specific action',
    signature: "actions('actionName')",
    category: 'workflow',
    example: "actions('Get_user')['status']",
    returnType: 'object',
    parameters: [
      { name: 'actionName', type: 'string', description: 'Name of the action', required: true },
    ],
  },
  {
    name: 'trigger',
    description: 'Return the trigger run details',
    signature: 'trigger()',
    category: 'workflow',
    example: "trigger()['name']",
    returnType: 'object',
    parameters: [],
  },

  // =============================================================================
  // URI PARSING FUNCTIONS
  // =============================================================================
  {
    name: 'uriHost',
    description: 'Returns the host name from a URI',
    signature: 'uriHost(uri)',
    category: 'uriParsing',
    example: 'new URL(\'https://example.com:8080/path\').hostname // "example.com"',
    returnType: 'string',
    parameters: [
      { name: 'uri', type: 'string', description: 'The URI to parse', required: true },
    ],
  },
  {
    name: 'uriPath',
    description: 'Returns the path from a URI',
    signature: 'uriPath(uri)',
    category: 'uriParsing',
    example: 'new URL(\'https://example.com/path/to/page\').pathname // "/path/to/page"',
    returnType: 'string',
    parameters: [
      { name: 'uri', type: 'string', description: 'The URI to parse', required: true },
    ],
  },
  {
    name: 'uriPathAndQuery',
    description: 'Returns the path and query from a URI',
    signature: 'uriPathAndQuery(uri)',
    category: 'uriParsing',
    example: "const u = new URL(uri); u.pathname + u.search",
    returnType: 'string',
    parameters: [
      { name: 'uri', type: 'string', description: 'The URI to parse', required: true },
    ],
  },
  {
    name: 'uriPort',
    description: 'Returns the port from a URI',
    signature: 'uriPort(uri)',
    category: 'uriParsing',
    example: 'new URL(\'https://example.com:8080/path\').port // "8080"',
    returnType: 'string',
    parameters: [
      { name: 'uri', type: 'string', description: 'The URI to parse', required: true },
    ],
  },
  {
    name: 'uriScheme',
    description: 'Returns the protocol scheme from a URI',
    signature: 'uriScheme(uri)',
    category: 'uriParsing',
    example: 'new URL(\'https://example.com\').protocol // "https:"',
    returnType: 'string',
    parameters: [
      { name: 'uri', type: 'string', description: 'The URI to parse', required: true },
    ],
  },
  {
    name: 'uriQuery',
    description: 'Returns the query string from a URI',
    signature: 'uriQuery(uri)',
    category: 'uriParsing',
    example: 'new URL(\'https://example.com?a=1&b=2\').search // "?a=1&b=2"',
    returnType: 'string',
    parameters: [
      { name: 'uri', type: 'string', description: 'The URI to parse', required: true },
    ],
  },

  // =============================================================================
  // MANIPULATION FUNCTIONS
  // =============================================================================
  {
    name: 'addProperty',
    description: 'Adds a property to an object and returns the new object',
    signature: 'addProperty(object, property, value)',
    category: 'manipulation',
    example: "{ ...obj, newKey: 'newValue' }",
    returnType: 'object',
    parameters: [
      { name: 'object', type: 'object', description: 'The source object', required: true },
      { name: 'property', type: 'string', description: 'The property name to add', required: true },
      { name: 'value', type: 'any', description: 'The value for the new property', required: true },
    ],
  },
  {
    name: 'setProperty',
    description: 'Sets a property on an object and returns the updated object',
    signature: 'setProperty(object, property, value)',
    category: 'manipulation',
    example: "{ ...obj, existingKey: 'updatedValue' }",
    returnType: 'object',
    parameters: [
      { name: 'object', type: 'object', description: 'The source object', required: true },
      { name: 'property', type: 'string', description: 'The property name to set', required: true },
      { name: 'value', type: 'any', description: 'The new value for the property', required: true },
    ],
  },
  {
    name: 'removeProperty',
    description: 'Removes a property from an object and returns the result',
    signature: 'removeProperty(object, property)',
    category: 'manipulation',
    example: "const { propToRemove, ...rest } = obj; // rest has no propToRemove",
    returnType: 'object',
    parameters: [
      { name: 'object', type: 'object', description: 'The source object', required: true },
      { name: 'property', type: 'string', description: 'The property name to remove', required: true },
    ],
  },
  {
    name: 'xpath',
    description: 'Evaluates an XPath expression against XML',
    signature: 'xpath(xml, expression)',
    category: 'manipulation',
    example: "// Use DOMParser + document.evaluate for XPath queries",
    returnType: 'any',
    parameters: [
      { name: 'xml', type: 'string', description: 'The XML document or string', required: true },
      { name: 'expression', type: 'string', description: 'The XPath expression to evaluate', required: true },
    ],
  },
  {
    name: 'mergeObjects',
    description: 'Merges two or more objects into one',
    signature: 'mergeObjects(object1, object2)',
    category: 'manipulation',
    example: "{ ...obj1, ...obj2 } or Object.assign({}, obj1, obj2)",
    returnType: 'object',
    parameters: [
      { name: 'object1', type: 'object', description: 'The first object', required: true },
      { name: 'object2', type: 'object', description: 'The second object to merge', required: true },
    ],
  },
];

/**
 * Only these curated helpers are currently executable in Otto's JavaScript
 * expression runtime. Keep picker/autocomplete scoped to this subset so we
 * don't suggest functions that fail at runtime.
 */
const JAVASCRIPT_SUPPORTED_FUNCTION_NAMES: ReadonlySet<string> = new Set([
  'trigger',
  'triggerBody',
  'triggerOutputs',
  'body',
  'outputs',
  'actions',
  'variables',
  'parameters',
  'item',
  'items',
  'iterationIndexes',
]);

export const JAVASCRIPT_EXPRESSION_FUNCTIONS: ExpressionFunctionInfo[] =
  EXPRESSION_FUNCTIONS.filter((fn) =>
    JAVASCRIPT_SUPPORTED_FUNCTION_NAMES.has(fn.name)
  );

/**
 * Group functions by category
 */
export function getFunctionsByCategory(): Record<ExpressionFunctionCategory, ExpressionFunctionInfo[]> {
  const grouped: Record<ExpressionFunctionCategory, ExpressionFunctionInfo[]> = {
    string: [],
    collection: [],
    logical: [],
    conversion: [],
    math: [],
    datetime: [],
    reference: [],
    workflow: [],
    uriParsing: [],
    manipulation: [],
  };

  for (const func of JAVASCRIPT_EXPRESSION_FUNCTIONS) {
    grouped[func.category].push(func);
  }

  return grouped;
}

/**
 * Search functions by name or description
 */
export function searchFunctions(query: string): ExpressionFunctionInfo[] {
  const lowerQuery = query.toLowerCase();
  return JAVASCRIPT_EXPRESSION_FUNCTIONS.filter(
    (func) =>
      func.name.toLowerCase().includes(lowerQuery) ||
      func.description.toLowerCase().includes(lowerQuery)
  );
}

/**
 * Get function info by name
 */
export function getFunctionByName(name: string): ExpressionFunctionInfo | undefined {
  return JAVASCRIPT_EXPRESSION_FUNCTIONS.find(
    (func) => func.name.toLowerCase() === name.toLowerCase()
  );
}

/**
 * Category display names
 */
export const CATEGORY_LABELS: Record<ExpressionFunctionCategory, string> = {
  string: 'String Functions',
  collection: 'Collection Functions',
  logical: 'Logical Functions',
  conversion: 'Conversion Functions',
  math: 'Math Functions',
  datetime: 'Date and Time',
  reference: 'Reference Functions',
  workflow: 'Workflow Functions',
  uriParsing: 'URI Parsing Functions',
  manipulation: 'Manipulation Functions',
};

/**
 * Categories that should be grouped under "Workflow" in pickers — these
 * surface workflow-context lookups (trigger, body, outputs, variables, …)
 * that mirror the runtime evaluator's first-class helpers.
 */
const WORKFLOW_PICKER_CATEGORIES: ReadonlySet<ExpressionFunctionCategory> = new Set([
  'workflow',
  'reference',
]);

/**
 * Group label used by the inline `${}` autocomplete. The `FunctionPickerPanel`
 * uses 'Workflow' / 'Global Functions'; we mirror those here so the two
 * surfaces feel like one library viewed two ways.
 */
export type ExpressionFunctionGroupLabel = 'Workflow' | 'Global Functions';

export const getExpressionFunctionGroup = (
  fn: ExpressionFunctionInfo,
): ExpressionFunctionGroupLabel =>
  WORKFLOW_PICKER_CATEGORIES.has(fn.category) ? 'Workflow' : 'Global Functions';

/**
 * Build the `insertText` for an `ExpressionFunctionInfo`. Falls back to the
 * authored signature when the function takes no parameters (e.g. `uuid()`).
 */
export const buildExpressionFunctionTemplate = (
  fn: ExpressionFunctionInfo,
): string => {
  if (fn.parameters && fn.parameters.length > 0) {
    const args = fn.parameters.map((p) => p.name).join(', ');
    return `${fn.name}(${args})`;
  }
  return fn.signature;
};

/**
 * Loose-shape autocomplete suggestion that the inline `${}` autocomplete
 * consumes. Defined here (rather than imported from `@/utils/expressions`)
 * to keep this module dependency-free; the field carries plain primitives.
 */
export interface ExpressionFunctionSuggestion {
  label: string;
  type: 'function';
  detail: string;
  documentation?: string;
  insertText: string;
  category: ExpressionFunctionGroupLabel;
  returnType?: string;
  args?: { name: string; type: string; optional?: boolean; description?: string }[];
}

/**
 * Convert one curated function definition into an autocomplete suggestion.
 * Used by the inline `${}` autocomplete so it shows the same library as
 * the rail's "fx" tab. Both surfaces stay consistent at a single source.
 */
export const expressionFunctionToSuggestion = (
  fn: ExpressionFunctionInfo,
): ExpressionFunctionSuggestion => {
  const args = fn.parameters?.map((p) => ({
    name: p.name,
    type: p.type,
    optional: !p.required,
    ...(p.description ? { description: p.description } : {}),
  }));
  return {
    label: fn.name,
    type: 'function',
    detail: fn.signature,
    documentation: fn.description,
    insertText: buildExpressionFunctionTemplate(fn),
    category: getExpressionFunctionGroup(fn),
    returnType: fn.returnType,
    ...(args && args.length > 0 ? { args } : {}),
  };
};

/**
 * Get all curated functions as autocomplete suggestions. Returns a fresh
 * array every call so callers can safely mutate / filter without affecting
 * the source data.
 */
export const getAllExpressionFunctionSuggestions = (): ExpressionFunctionSuggestion[] =>
  JAVASCRIPT_EXPRESSION_FUNCTIONS.map(expressionFunctionToSuggestion);
